import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, post } from './client';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  hasPassword: boolean;
}

export interface MeResponse {
  user: CurrentUser | null;
  googleEnabled: boolean;
}

export interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  summary: string | null;
  createdAt: string;
  author: { id: string; name: string } | null;
}

/** Траты за период: главный итог в рублях, доллары второй строкой (docs/04-finance.md). */
export interface PeriodSpend {
  from: string;
  to: string;
  /** Рубли в копейках. */
  amountMinor: number;
  usdMinor: number;
  services: { rubMinor: number; usdMinor: number };
  other: { rubMinor: number; usdMinor: number; count: number };
  /** Расход сервисов без курса: в рублёвый итог не вошёл. */
  unratedUsdMinor: number;
}

export interface SpendSummary {
  currency: 'RUB';
  current: PeriodSpend;
  previous: PeriodSpend;
  changePercent: number | null;
  hasData: boolean;
  excludedServices: number;
}

// ------------------------------------------------------------------- деньги

export type WalletKind = 'virtual_card' | 'crypto' | 'direct_card' | 'other';
export type FeeKind = 'fixed' | 'percent' | 'none';
export type BillingModel = 'prepaid_balance' | 'postpaid_invoice' | 'subscription';
export type Provider = 'openai' | 'anthropic' | 'digitalocean' | 'google_workspace' | 'manual';

export type WalletBalanceMode = 'computed' | 'anchored' | 'provider' | 'hidden';
export type RatePolicy = 'market' | 'topups';

export interface WalletSummary {
  id: string;
  /** Код описания в реестре кошельков. Пусто у кошелька, заведённого до реестра. */
  code: string | null;
  name: string;
  kind: WalletKind;
  currency: string;
  isActive: boolean;
  notes: string | null;
  /** Остаток по правилу кошелька. Пусто, если честной цифры нет. */
  balanceMinor: number | null;
  balanceMode: WalletBalanceMode;
  balanceAt: string | null;
  /** Откуда остаток — одной строкой под цифрой. */
  balanceNote: string;
  sourceTitle: string | null;
  ratePolicy: RatePolicy;
  manualTopUps: boolean;
  topUpsNote: string | null;
  defaultFeeMinor: number;
  lowBalanceAlert: boolean;
  averageRate: number | null;
  lastTopUpAt: string | null;
  topUpCount: number;
  /** Сбор из источника данных. У кошелька без источника пусто. */
  sync: { source: string; lastSyncAt: string | null; status: string; error: string | null } | null;
}

export interface WalletTopUpRow {
  id: string;
  occurredAt: string;
  creditedAmount: number;
  paidAmount: number;
  paidCurrency: string;
  feeKind: FeeKind;
  feeValue: number;
  effectiveRate: number | null;
  comment: string | null;
  author: string | null;
}

export type RecordSource = 'manual' | 'forward' | 'chain';

export interface WalletPaymentRow {
  id: string;
  occurredAt: string;
  amount: number;
  fee: number;
  serviceId: string;
  serviceName: string;
  rubCost: number | null;
  rateSource: string;
  source: RecordSource | string;
  author: string | null;
}

export interface WalletExpenseRow {
  id: string;
  date: string;
  amount: number;
  currency: string;
  rubCost: number | null;
  comment: string;
}

/** Операция из источника данных. У ждущей решения сумма и получатель скрыты. */
export interface WalletOperationRow {
  id: string;
  occurredAt: string;
  kind: 'payment' | 'topup' | 'swap' | 'expense' | 'unknown' | string;
  amount: number | null;
  fee: number;
  asset: string;
  counterparty: string | null;
  serviceName: string | null;
  expenseComment: string | null;
  problem: string | null;
}

export interface WalletDetail extends WalletSummary {
  topUps: WalletTopUpRow[];
  payments: WalletPaymentRow[];
  expenses: WalletExpenseRow[];
  operations: WalletOperationRow[];
}

export interface WalletIntegrationRow {
  id: string;
  code: string;
  name: string;
  source: string;
  sourceTitle: string;
  lastSyncAt: string | null;
  lastSyncStatus: string;
  lastSyncError: string | null;
  failureStreak: number;
}

export interface Forecast {
  days: number | null;
  averageDaily: number;
  daysWithSpending: number;
  windowDays: number;
  isApproximate: boolean;
}

export interface ServiceSummary {
  id: string;
  name: string;
  provider: Provider;
  billingModel: BillingModel;
  currency: string;
  isActive: boolean;
  sortOrder: number;
  forecastWindowDays: number;
  defaultWalletId: string | null;
  defaultWalletName: string | null;
  balanceMinor: number | null;
  balanceIsCalculated: boolean;
  /** Страница кабинета провайдера, где виден настоящий остаток. */
  balanceUrl: string | null;
  balanceUrlIsCustom: boolean;
  forecast: Forecast | null;
  lastReconciledAt: string | null;
  usageThroughDate: string | null;
  syncEnabled: boolean;
  syncStatus: string;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  supportsSync: boolean;
  needsCredential: boolean;
  providerTitle: string;
  credential: {
    maskedHint: string;
    label: string;
    scope: string;
    isValid: boolean | null;
    lastCheckedAt: string | null;
  } | null;
  providerBalanceMinor: number | null;
  providerPendingMinor: number | null;
  providerBalanceAt: string | null;
  subscription: {
    amount: number;
    period: 'monthly' | 'yearly';
    nextChargeAt: string;
    seats: number | null;
  } | null;
}

export interface ServiceUsageRow {
  id: string;
  date: string;
  amount: number;
  source: string;
  note: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  cachedTokens: number | null;
  requestCount: number | null;
}

export interface ServiceTopUpRow {
  id: string;
  occurredAt: string;
  amount: number;
  fee: number;
  walletId: string | null;
  walletName: string | null;
  rubCost: number | null;
  rateSource: string;
  rateE4: number | null;
  rateAt: string | null;
  source: RecordSource | string;
  comment: string | null;
  author: string | null;
}

// ------------------------------------------------------------ прочие расходы

export interface ExpenseRow {
  id: string;
  date: string;
  amount: number;
  currency: string;
  rubCost: number | null;
  comment: string;
  source: 'manual' | 'telegram' | 'wallet' | string;
  walletName: string | null;
  author: string | null;
}

export interface ExpenseList {
  rows: ExpenseRow[];
  monthRubMinor: number;
  monthCount: number;
}

// ------------------------------------------------------------------ курс дня

export type RateSourceKind = 'p2p' | 'cbr';

export interface RateOverview {
  current: { rateE4: number; source: RateSourceKind; observedAt: string } | null;
  p2p: {
    rateE4: number;
    observedAt: string;
    details: { ads?: number; amountRub?: number; min?: number; max?: number } | null;
  } | null;
  cbr: { rateE4: number; observedAt: string; origin: string | null } | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  failureStreak: number;
  error: string | null;
}

export interface RatePreview {
  rate: { rateE4: number; source: 'manual' | 'wallet_average' | 'p2p' | 'cbr'; at: string } | null;
}

export interface AdjustmentRow {
  id: string;
  occurredAt: string;
  actualBalance: number;
  calculatedBalance: number;
  delta: number;
  comment: string | null;
  author: string | null;
}

export interface InvoiceRow {
  id: string;
  externalId: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  status: string;
  url: string | null;
}

export interface ModelUsageRow {
  model: string;
  amount: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  requestCount: number;
}

export interface ServiceDetail extends ServiceSummary {
  usage: ServiceUsageRow[];
  usageByDay: Array<{ date: string; amount: number }>;
  byModel: ModelUsageRow[];
  invoices: InvoiceRow[];
  topUps: ServiceTopUpRow[];
  adjustments: AdjustmentRow[];
  averageServiceRate: number | null;
}

export interface IntegrationService {
  id: string;
  name: string;
  isActive: boolean;
  syncEnabled: boolean;
  hasCredential: boolean;
  credentialValid: boolean | null;
  maskedHint: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string;
  lastSyncError: string | null;
  usageRecords: number;
  usageThroughDate: string | null;
}

export interface IntegrationRow {
  provider: string;
  title: string;
  supportsSync: boolean;
  needsCredential: boolean;
  credentialHint: string;
  services: IntegrationService[];
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  failureStreak: number;
  error: string | null;
  backfill: { maxDays: number; chunkDays: number };
}

export interface SyncRunResult {
  serviceId: string;
  serviceName: string;
  status: 'ok' | 'failed' | 'skipped';
  usageRecords: number;
  invoices: number;
  error: string | null;
  reason?: string;
}

// -------------------------------------------------------------------- задачи

export type TaskStatus = 'not_started' | 'in_progress' | 'done';
export type TaskDue = 'overdue' | 'this_week' | 'this_month' | 'no_date';

export interface TaskAssignee {
  notionUserId: string;
  name: string;
  avatarUrl: string | null;
  userId: string | null;
}

export interface TaskRow {
  id: string;
  notionPageId: string;
  title: string;
  status: TaskStatus;
  assignees: TaskAssignee[];
  dueDate: string | null;
  dueDateEnd: string | null;
  projectId: string | null;
  projectName: string | null;
  comment: string | null;
  notionUrl: string;
  isArchived: boolean;
  notionLastEditedAt: string;
}

export interface TasksResponse {
  tasks: TaskRow[];
  withoutDueDate: number;
  people: Array<{
    notionUserId: string;
    name: string;
    avatarUrl: string | null;
    userId: string | null;
    userName: string | null;
    email: string | null;
    activeTasks: number;
  }>;
  projects: Array<{ id: string; name: string; color: string | null }>;
  sync: {
    lastRunAt: string | null;
    lastSuccessAt: string | null;
    status: string;
    error: string | null;
    failureStreak: number;
    configured: boolean;
  };
}

export interface TaskFilterState {
  assignee: string;
  status: string;
  projectId: string;
  due: string;
  includeDone: boolean;
}

export interface PeopleResponse {
  users: Array<{
    id: string;
    name: string;
    email: string;
    notionUserId: string | null;
    notionName: string | null;
    telegramLinked: boolean;
  }>;
  notionPeople: Array<{
    notionUserId: string;
    name: string;
    email: string | null;
    avatarUrl: string | null;
    lastSeenAt: string;
    isLinked: boolean;
  }>;
}

export interface ProjectRow {
  id: string;
  name: string;
  notionOptionName: string;
  color: string | null;
  isActive: boolean;
  taskCount: number;
}

export interface NotionSyncResult {
  mode: string;
  fetched: number;
  created: number;
  updated: number;
  archived: number;
  people: number;
  projectsCreated: number;
  error: string | null;
}

// ------------------------------------------------------------------ обзор

export interface TriggeredThreshold {
  type: string;
  level: 'warning' | 'critical';
  message: string;
}

export interface OverviewService extends ServiceSummary {
  lastTopUpMinor: number | null;
  lastTopUpAt: string | null;
  fillPercent: number | null;
  triggered: TriggeredThreshold[];
}

export interface OverviewPerson {
  userId: string | null;
  name: string;
  notionUserId: string | null;
  inProgress: TaskRow[];
}

export interface Overview {
  services: OverviewService[];
  burning: { overdue: TaskRow[]; soon: TaskRow[]; totalOverdue: number };
  people: OverviewPerson[];
  spend: {
    currency: 'RUB';
    currentMinor: number;
    currentUsdMinor: number;
    previousMinor: number;
    changePercent: number | null;
    hasData: boolean;
    other: { rubMinor: number; count: number };
    unratedUsdMinor: number;
    byDay: Array<{ date: string; amount: number }>;
  };
  notifications: Array<{
    id: string;
    type: string;
    level: string;
    title: string;
    body: string;
    createdAt: string;
  }>;
  tasksSync: { lastSuccessAt: string | null; status: string };
}

// -------------------------------------------------------------------- хуки

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/auth/me'),
    staleTime: 30_000,
    retry: false,
  });
}

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      post<{ user: CurrentUser }>('/auth/login', input),
    onSuccess: () => client.invalidateQueries(),
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ ok: boolean }>('/auth/logout'),
    onSuccess: () => client.invalidateQueries(),
  });
}

/**
 * Любое изменяющее действие вызывается по имени операции. Тот же список
 * операций позже получит ассистент (docs/12-extensibility.md).
 */
export function useOperation<TInput, TResult = { id: string }>(name: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => post<TResult>(`/operations/${name}`, input ?? {}),
    onSuccess: () => client.invalidateQueries(),
  });
}

export interface ProfileResponse {
  user: CurrentUser;
  activeSessions: number;
}

export function useProfile(enabled: boolean) {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => api<ProfileResponse>('/profile'),
    enabled,
  });
}

export function useAudit(filters: { userId?: string; action?: string }, enabled: boolean) {
  const params = new URLSearchParams();
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.action) params.set('action', filters.action);
  const query = params.toString();
  return useQuery({
    queryKey: ['audit', filters.userId ?? '', filters.action ?? ''],
    queryFn: () =>
      api<{ rows: AuditRow[]; nextCursor: string | null }>(`/audit${query ? `?${query}` : ''}`),
    enabled,
  });
}

export function useAuditFilters(enabled: boolean) {
  return useQuery({
    queryKey: ['audit', 'filters'],
    queryFn: () =>
      api<{ actions: string[]; authors: Array<{ id: string; name: string }> }>('/audit/filters'),
    enabled,
  });
}

export function useSpendSummary(enabled: boolean) {
  return useQuery({
    queryKey: ['spend-summary'],
    queryFn: () => api<SpendSummary>('/overview/spend-summary'),
    enabled,
  });
}

export function useWallets() {
  return useQuery({
    queryKey: ['wallets'],
    queryFn: () => api<{ wallets: WalletSummary[] }>('/wallets'),
  });
}

export function useWallet(id: string | undefined) {
  return useQuery({
    queryKey: ['wallet', id ?? ''],
    queryFn: () => api<WalletDetail>(`/wallets/${id}`),
    enabled: Boolean(id),
  });
}

export function useServices() {
  return useQuery({
    queryKey: ['services'],
    queryFn: () => api<{ services: ServiceSummary[] }>('/services'),
  });
}

export function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: () =>
      api<{
        integrations: IntegrationRow[];
        wallets: WalletIntegrationRow[];
        schedulerEnabled: boolean;
        backfillDefaultDays: number;
      }>('/integrations'),
  });
}

export function useOverview() {
  return useQuery({ queryKey: ['overview'], queryFn: () => api<Overview>('/overview') });
}

export function useTasks(filters: TaskFilterState) {
  const params = new URLSearchParams();
  if (filters.assignee) params.set('assignee', filters.assignee);
  if (filters.status) params.set('status', filters.status);
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.due) params.set('due', filters.due);
  if (!filters.includeDone) params.set('includeDone', 'false');
  const query = params.toString();

  return useQuery({
    queryKey: ['tasks', query],
    queryFn: () => api<TasksResponse>(`/tasks${query ? `?${query}` : ''}`),
  });
}

export interface AttentionTask {
  id: string;
  title: string;
  status: TaskStatus;
  dueDate: string | null;
  dueDateEnd: string | null;
  projectName: string | null;
  notionUrl: string;
  assignees: Array<{ notionUserId: string; name: string; userId: string | null }>;
  days: number;
}

export interface AttentionReport {
  notStarted: AttentionTask[];
  overdue: AttentionTask[];
  asOf: string;
  total: number;
}

/** Задачи, выбившиеся из срока. Тот же отчёт отдаёт команда бота. */
export function useAttention() {
  return useQuery({
    queryKey: ['tasks', 'attention'],
    queryFn: () => api<AttentionReport>('/tasks/attention'),
  });
}

export interface SourceFreshness {
  source: string;
  title: string;
  lastSuccessAt: string | null;
  status: string;
  failureStreak: number;
}

export interface Freshness {
  sources: SourceFreshness[];
  newestAt: string | null;
  oldest: SourceFreshness | null;
  failing: SourceFreshness[];
}

/** Возраст данных для подписи в шапке. Нужен на каждом экране. */
export function useFreshness(enabled: boolean) {
  return useQuery({
    queryKey: ['freshness'],
    queryFn: () => api<Freshness>('/freshness'),
    enabled,
    refetchInterval: 120_000,
  });
}

export function usePeople() {
  return useQuery({ queryKey: ['people'], queryFn: () => api<PeopleResponse>('/people') });
}

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api<{ projects: ProjectRow[] }>('/projects'),
  });
}

// -------------------------------------------------------------- уведомления

export type NotificationLevel = 'info' | 'warning' | 'critical';
export type NotificationChannel = 'in_app' | 'telegram' | 'web_push';
export type NotificationGroup = 'money' | 'tasks' | 'system';

export interface NotificationRow {
  id: string;
  type: string;
  typeTitle: string;
  group: NotificationGroup;
  groupTitle: string;
  level: NotificationLevel;
  title: string;
  body: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedReason: string | null;
  readAt: string | null;
  href: string;
  channels: Array<{ channel: NotificationChannel; status: string; error: string | null }>;
}

export interface NotificationList {
  notifications: NotificationRow[];
  activeCount: number;
  unreadCount: number;
}

export interface NotificationTypeSetting {
  type: string;
  title: string;
  description: string;
  group: NotificationGroup;
  groupTitle: string;
  level: NotificationLevel;
  personal: boolean;
  isEnabled: boolean;
  channels: NotificationChannel[];
  defaultChannels: NotificationChannel[];
}

export interface ThresholdSetting {
  key: 'absolute' | 'days_left' | 'percent_of_topup' | 'spike';
  title: string;
  hint: string;
  unit: 'money' | 'days' | 'percent' | 'times';
  value: number;
  defaultValue: number;
  serviceId: string | null;
}

export type DigestFrequency = 'off' | 'daily' | 'weekly' | 'biweekly';

export interface TaskDigestSettings {
  frequency: DigestFrequency;
  weekday: number;
  hour: number;
  lastSentOn: string | null;
}

export interface TelegramPlace {
  id: string;
  chatId: string;
  title: string;
  kind: string;
  isForum: boolean;
  addedAt: string;
  removedAt: string | null;
  notificationsEnabled: boolean;
  commandsEnabled: boolean;
  topics: Array<{
    id: string;
    threadId: number;
    name: string;
    notificationsEnabled: boolean;
    commandsEnabled: boolean;
    lastSeenAt: string;
  }>;
}

export interface NotificationSettings {
  types: NotificationTypeSetting[];
  thresholds: ThresholdSetting[];
  serviceOverrides: Array<{
    serviceId: string;
    serviceName: string;
    currency: string;
    thresholds: ThresholdSetting[];
  }>;
  telegram: {
    configured: boolean;
    linked: boolean;
    username: string | null;
    linkedAt: string | null;
    botUsername: string | null;
    pendingCode: string | null;
    pendingCodeExpiresAt: string | null;
  };
  webPush: {
    configured: boolean;
    publicKey: string | null;
    devices: Array<{ id: string; userAgent: string | null; createdAt: string; lastOkAt: string | null }>;
  };
  places: TelegramPlace[];
  taskDigest: TaskDigestSettings;
}

export interface NotificationFilterState {
  tab: 'active' | 'history';
  group?: NotificationGroup | undefined;
  level?: NotificationLevel | undefined;
  unreadOnly?: boolean | undefined;
}

export function useNotifications(filters: NotificationFilterState) {
  const params = new URLSearchParams({ tab: filters.tab });
  if (filters.group) params.set('group', filters.group);
  if (filters.level) params.set('level', filters.level);
  if (filters.unreadOnly) params.set('unreadOnly', 'true');
  const query = params.toString();

  return useQuery({
    queryKey: ['notifications', query],
    queryFn: () => api<NotificationList>(`/notifications?${query}`),
  });
}

/** Счётчик для значка в навигации: нужен на каждом экране. */
export function useUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api<{ count: number }>('/notifications/unread'),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useNotificationSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: () => api<NotificationSettings>('/notifications/settings'),
    enabled,
  });
}

export function useExpenses() {
  return useQuery({ queryKey: ['expenses'], queryFn: () => api<ExpenseList>('/expenses') });
}

export function useRates(enabled = true) {
  return useQuery({ queryKey: ['rates'], queryFn: () => api<RateOverview>('/rates'), enabled });
}

/** Какой курс возьмёт пополнение сервиса: подсказка в диалоге до сохранения. */
export function useRatePreview(walletId: string | null, date: string, enabled: boolean) {
  const params = new URLSearchParams({ date });
  if (walletId) params.set('walletId', walletId);
  return useQuery({
    queryKey: ['rates', 'preview', walletId ?? '', date],
    queryFn: () => api<RatePreview>(`/rates/preview?${params.toString()}`),
    enabled: enabled && /^\d{4}-\d{2}-\d{2}$/.test(date),
    staleTime: 60_000,
  });
}

export function useService(id: string | undefined) {
  return useQuery({
    queryKey: ['service', id ?? ''],
    queryFn: () => api<ServiceDetail>(`/services/${id}`),
    enabled: Boolean(id),
  });
}
